import { HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../environments/environment';
import { AccessMatrixComponent } from './admin/access-matrix.component';
import { BatchExportComponent } from './admin/batch-export.component';
import { ExportQueueComponent } from './admin/export-queue.component';
import { TargetedEditComponent } from './admin/targeted-edit.component';
import { UserAdminComponent } from './admin/user-admin.component';
import { WorkflowAddComponent } from './admin/workflow-add/workflow-add.component';
import { WorkflowAdminComponent } from './admin/workflow-admin.component';
import { AppErrorComponent } from './app-error.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { MasterCalcComponent } from './calculations/master-calc.component';
import { API_BASE_URL, ClientApi } from './client-api.service';
import { CommandHistoryComponent } from './commands/command-history.component';
import { CommentsModule } from './comments/comments.module';
import { ControlsModule } from './controls/controls.module';
import { DirectivesModule } from './directives/directives.module';
import { AccountHistoryComponent } from './ledger-menu/account-history.component';
import { LedgerMenuComponent } from './ledger-menu/ledger-menu.component';
import { ManualExportComponent } from './ledger-menu/manual-export.component';
import { WorkflowManagementComponent } from './ledger-menu/workflow-management.component';
import { ComparisonMenuSectionComponent } from './ledgers/comparisons/comparison-menu-section/comparison-menu-section.component';
import { HomeComponent } from './ledgers/home.component';
import { LedgerAccountTitleComponent } from './ledgers/ledger-account-title.component';
import { LedgerAccountComponent } from './ledgers/ledger-account.component';
import { LedgerComponent } from './ledgers/ledger.component';
import { MenuTrayModule } from './menu-tray/menu-tray.module';
import { PayrollEmployeeControlComponent } from './payroll/payroll-employee-control.component';
import { PayrollComponent } from './payroll/payroll.component';
import { ReportCubeDisplayComponent } from './reports/report-cube-display.component';
import { ReportsComponent } from './reports/reports.component';
import { RevenueModule } from './revenue/revenue.module';
import { DynamicNumberPipe } from './shared/dynamic-number.pipe';
import { GLOBAL, IGlobalSettings } from './shared/global-settings';
import { RequestAccessFormComponent } from './ledgers/request-access-form/request-access-form.component';
import { ConfigService } from './config.service';
import { FinancialPlanningMaintenanceMenuComponent } from './admin/maintenance/financial-planning-maintenance-menu.component';
import { FinancialPlanningInstallerComponent } from './admin/maintenance/installer/financial-planning-installer.component';
import { FinancialPlanningInstallerTrackingComponent } from './admin/maintenance/installer/financial-planning-installer-tracking.component';
import { ApplicationViewsComponent } from './admin/maintenance/ApplicationViews/application-views.component';
import { ApplicationViewsUpsertComponent } from './admin/maintenance/ApplicationViews/application-views-upsert.component';
import { PositionTypesComponent } from './admin/maintenance/positionTypes/position-types/position-types.component';
import { PositionTypesUpsertComponent } from './admin/maintenance/positionTypes/position-types/position-types-upsert.component';
import { SystemConfigComponent } from './admin/maintenance/systemConfig/system-config.component';
import { OrganizationsComponent } from './admin/maintenance/organizations/organizations.component';
import { OrganizationUpsertComponent } from './admin/maintenance/organizations/organization-upsert/organization-upsert.component';
import { AccountSplitComponent } from './admin/maintenance/management-fee-splits/account-split.component';
import { AccountSplitUpsertComponent } from './admin/maintenance/management-fee-splits/account-splits-upsert/account-split-upsert.component';
import { ManagementFeeAccountSplitsComponent } from './admin/maintenance/management-fee-splits/management-fee-account-splits/management-fee-account-splits.component';
import { ManagementFeeAccountSplitsListComponent } from './admin/maintenance/management-fee-splits/management-fee-account-splits-list/management-fee-account-splits-list.component';
import { LedgerViewsComponent } from './admin/maintenance/ledger-views/ledger-views.component';
import { LedgerViewInfoUpsertComponent } from './admin/maintenance/ledger-views/ledger-view-info-upsert/ledger-view-info-upsert.component';
import { ViewAccountsComponent } from './admin/maintenance/ledger-views/view-accounts/view-accounts.component';
import { AddAccountsDialogComponent } from './admin/maintenance/ledger-views/add-accounts-dialog/add-accounts-dialog.component';
import { AUTH_CONFIG, AuthConfig, normalizeScopesArray, SESSION_POST_LOGIN_REDIRECT_URL } from './auth/auth-config';
import { MSAL_GUARD_CONFIG,
         MSAL_INSTANCE,
         MSAL_INTERCEPTOR_CONFIG,
         MsalGuardConfiguration,
         MsalInterceptorConfiguration} from '@azure/msal-angular';
import { BrowserCacheLocation,
         BrowserUtils,
         Configuration,
         InteractionType,
         IPublicClientApplication,
         LogLevel,
         PublicClientApplication } from '@azure/msal-browser';
import { AuthModule } from './auth/auth.module';
import { TccUiModule } from './shared/tcc-ui/tcc-ui.module';
import { AppInsightsModule, AppInsightsTelemtryItem } from './shared/tcc-ui/app-insights';
import { ScrollService } from './shared/tcc-ui/scroll.service';
import { TemplateUtilService } from './shared/tcc-ui/template-util.service';
import { FileDownloadService } from './shared/tcc-ui/file-download.service';
import { GenericCanDeactivateGuard } from './shared/tcc-ui/generic-can-deactivate.guard';
import { OrgMappingLookupComponent } from './admin/maintenance/org-mapping-lookup/org-mapping-lookup.component';
import { OrgMappingLookupUpsertComponent } from './admin/maintenance/org-mapping-lookup/org-mapping-lookup-upsert/org-mapping-lookup-upsert.component';
import { AddGroupKeyComponent } from './admin/maintenance/FPLists/addGroupKey/add-group-key.component';
import { FinancialPlanningListsComponent } from './admin/maintenance/FPLists/financial-planning-lists.component';
import { AddGroupKeyMemberComponent } from './admin/maintenance/FPLists/addGroupKeyMember/add-group-key-member.component';
import { CloneGroupKeyComponent } from './admin/maintenance/FPLists/cloneGroupKey/clone-group-key.component';




export function aiTelemInitializer(env: AppInsightsTelemtryItem) {
  env.tags!['ai.cloud.role'] = 'Tcc.Fp.App';
}

// disable the auth if in an iframe or popup
const AUTH_DISABLED = BrowserUtils.isInIframe() || BrowserUtils.isInPopup();

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

function msalInstanceFactory(authConfig: AuthConfig): IPublicClientApplication {
  const config: Configuration = {
    auth: {
      authority: environment.msalConfig.auth.authority || 'https://login.microsoftonline.com/f5c71729-7816-4984-867a-0b3a69f99ff8/',
      clientId: environment.msalConfig.auth.clientId,
      redirectUri: '/',
      postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    }
  };
  if (authConfig?.logLevel) {
    // if config.system is set to undefined it throws an error.  That is why it is added here.
    config.system = {
      loggerOptions: {
        loggerCallback: (_, message) => console.log(message),
        logLevel: authConfig?.logLevel || 2,
        piiLoggingEnabled: false
      }
    };
  }
  return new PublicClientApplication(config);
}

function msalInterceptorConfigFactory(authConfig: AuthConfig): MsalInterceptorConfiguration {
  // essentially disable the interceptor by not providing a resource map
  return AUTH_DISABLED
    ? { interactionType: InteractionType.Popup, protectedResourceMap: new Map() }
    : {
      interactionType: InteractionType.Redirect,
      protectedResourceMap: new Map(Array.from(authConfig.protectedResourceMap.entries())
        .map(([res, scopesOrEmpty]) => [res, normalizeScopesArray(scopesOrEmpty)]))
    };
}

function msalGuardConfigFactory(authConfig: AuthConfig): MsalGuardConfiguration {
  const scopes = Array.from(authConfig.protectedResourceMap.values())
    .filter((x): x is NonNullable<typeof x> => !!x) // remove null values.
    .reduce((acc, cur) => acc.concat(cur), [] as string[]) // flatten arrays
    .reduce((acc, cur) => acc.includes(cur) ? acc : acc.concat([cur]), [] as string[]); // make distinct

  return AUTH_DISABLED
      ? { interactionType: InteractionType.Popup }
      : {
        interactionType: InteractionType.Redirect,
        authRequest: (_, route) => {
          sessionStorage.setItem(SESSION_POST_LOGIN_REDIRECT_URL, route.url);
          return {
            scopes
          };
        },
        loginFailedRoute: authConfig?.unauthorizedRoute || '/auth/unauthorized'
      };
}

@NgModule({
  imports: [
    BrowserAnimationsModule,
    BrowserModule,    
    ControlsModule,
    DirectivesModule,
    FormsModule,
    HttpClientModule,
    ReactiveFormsModule,
    NgbTooltipModule,
    NgbDropdownModule,
    AppRoutingModule,
    TccUiModule.forRoot(),
    AppInsightsModule.forRoot(
      { instrumentationKey: environment.appInsightsId },
      [aiTelemInitializer]
    ),
    AuthModule.forRoot(),
    TccUiModule,
    MenuTrayModule,
    CommentsModule,
    RevenueModule
  ],
  providers: [
    ConfigService,
    {
      provide: APP_INITIALIZER,
      deps: [ConfigService],
      useFactory: (configService: ConfigService) => () => configService.load(environment),
      multi: true,
    },
    {
      provide: API_BASE_URL,
      deps: [ConfigService, APP_INITIALIZER],
      useFactory: (configService: ConfigService) =>
        configService?.get('apiBaseUrl'),
    },
    {
      provide: AUTH_CONFIG,
      deps: [ConfigService, APP_INITIALIZER],
      useFactory: (configService: ConfigService) =>
        configService?.getAuthConfig()
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: msalInstanceFactory,
      deps: [AUTH_CONFIG]
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: msalGuardConfigFactory,
      deps: [AUTH_CONFIG]
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: msalInterceptorConfigFactory,
      deps: [AUTH_CONFIG]
    },
    ClientApi,    
    FileDownloadService,
    GenericCanDeactivateGuard,
    ScrollService,
    TemplateUtilService,
    {
      provide: GLOBAL,
      useValue: <IGlobalSettings>{
        specialAccounts: {        
        }
      }
    }
  ],
  declarations: [
    AccessMatrixComponent,
    AccountHistoryComponent,
    AppComponent,
    AppErrorComponent,
    AddGroupKeyComponent,
    BatchExportComponent,
    DynamicNumberPipe,
    CommandHistoryComponent,
    ExportQueueComponent,
    FinancialPlanningListsComponent,
    HomeComponent,
    LedgerAccountComponent,
    LedgerAccountTitleComponent,
    LedgerComponent,
    LedgerMenuComponent,
    MasterCalcComponent,
    PayrollComponent,
    PayrollEmployeeControlComponent,
    ReportCubeDisplayComponent,
    ReportsComponent,
    TargetedEditComponent,
    UserAdminComponent,
    WorkflowAdminComponent,
    WorkflowManagementComponent,
    ManualExportComponent,
    ComparisonMenuSectionComponent,
    WorkflowAddComponent,
    RequestAccessFormComponent,
    FinancialPlanningMaintenanceMenuComponent,
    FinancialPlanningInstallerComponent,
    FinancialPlanningInstallerTrackingComponent,
    ApplicationViewsComponent,
    ApplicationViewsUpsertComponent,
    PositionTypesComponent,
    PositionTypesUpsertComponent,
    SystemConfigComponent,
    OrganizationsComponent,
    OrgMappingLookupComponent,
    OrgMappingLookupUpsertComponent,
    OrganizationUpsertComponent,
    AccountSplitComponent,
    AccountSplitUpsertComponent,
    ManagementFeeAccountSplitsComponent,
    ManagementFeeAccountSplitsListComponent,
    LedgerViewsComponent,
    LedgerViewInfoUpsertComponent,
    ViewAccountsComponent,
    AddAccountsDialogComponent,
    AddGroupKeyMemberComponent,
    CloneGroupKeyComponent
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
